import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Users, BookOpen, CheckCircle, Clock, ArrowRight, Plus, Bell, UserPlus, Copy, Gift, Trash2, BarChart3, Search, ChevronDown, ChevronUp } from "lucide-react";
import { API } from "../App";
import { TrialExpirationBanner } from "../components/TrialBanner";
import UpcomingAppointments from "../components/UpcomingAppointments";
import RecentSessionsCard from "../components/RecentSessionsCard";
import { useI18n } from "../lib/i18n";

const TherapistDashboard = ({ user, logout }) => {
  const { t } = useI18n();
  const [stats, setStats] = useState({ total_patients: 0, active_patients: 0, pending_assignments: 0, completed_today: 0 });
  const [reminderStats, setReminderStats] = useState({ total_reminders_sent: 0, reminders_today: 0, pending_reminders: 0 });
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exerciseSummaries, setExerciseSummaries] = useState({});
  const [ownerInvitations, setOwnerInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [exerciseSearch, setExerciseSearch] = useState("");
  const [exerciseListExpanded, setExerciseListExpanded] = useState(false);
  const isOwner = user?.email === "victor.loher@vmlh-tcc.fr";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, patientsRes, reminderStatsRes] = await Promise.all([
          axios.get(`${API}/therapist/stats`, { withCredentials: true }),
          axios.get(`${API}/therapist/patients`, { withCredentials: true }),
          axios.get(`${API}/therapist/reminders/stats`, { withCredentials: true }),
        ]);
        setStats(statsRes.data); setPatients(patientsRes.data); setReminderStats(reminderStatsRes.data);
        // Fetch exercise summaries for active patients
        const activePatients = patientsRes.data.filter(p => p.is_active);
        const summaries = {};
        await Promise.all(activePatients.slice(0, 10).map(async (p) => {
          try {
            const res = await axios.get(`${API}/therapist/patient-exercises/${p.user_id}`, { withCredentials: true });
            summaries[p.user_id] = res.data.summary;
          } catch {}
        }));
        setExerciseSummaries(summaries);
      } catch (error) { console.error("Error fetching data:", error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  // Fetch owner invitations
  useEffect(() => {
    if (!isOwner) return;
    axios.get(`${API}/therapist/owner/invitations`, { withCredentials: true })
      .then(res => setOwnerInvitations(res.data))
      .catch(() => {});
  }, [isOwner]);

  // Fetch referral info
  useEffect(() => {
    axios.get(`${API}/therapist/referral`, { withCredentials: true })
      .then(res => setReferralInfo(res.data))
      .catch(() => {});
  }, []);

  const handleOwnerInvite = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API}/therapist/owner/invite`, { email: inviteEmail, name: inviteName }, { withCredentials: true });
      const newInvite = res.data;
      setOwnerInvitations(prev => [{ code: newInvite.code, invited_email: inviteEmail, invited_name: inviteName, used: false, created_at: new Date().toISOString() }, ...prev]);
      const link = `${window.location.origin}/register?owner_invite=${newInvite.code}`;
      navigator.clipboard.writeText(link);
      setInviteEmail(""); setInviteName("");
      alert(t("owner.inviteCopied", "Lien d'invitation copié !"));
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setInviting(false);
  };

  const revokeInvite = async (code) => {
    try {
      await axios.delete(`${API}/therapist/owner/invitations/${code}`, { withCredentials: true });
      setOwnerInvitations(prev => prev.filter(i => i.code !== code));
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const copyReferralLink = () => {
    if (referralInfo?.referral_code) {
      navigator.clipboard.writeText(`${window.location.origin}/register?ref=${referralInfo.referral_code}`);
      alert(t("referral.copied", "Lien de parrainage copié !"));
    }
  };

  const statCards = [
    { label: t("therapist.activePatients"), value: stats.active_patients, icon: Users, color: "text-primary", link: "/therapist/patients" },
    { label: t("therapist.totalPatients"), value: stats.total_patients, icon: Users, color: "text-muted-foreground" },
    { label: t("therapist.pendingExercises"), value: stats.pending_assignments, icon: Clock, color: "text-amber-500" },
    { label: t("therapist.completedToday"), value: stats.completed_today, icon: CheckCircle, color: "text-accent" },
  ];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6 sm:space-y-8" data-testid="therapist-dashboard">
        <TrialExpirationBanner />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("therapist.hello")} {user?.name?.split(' ')[0]}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("therapist.overview")}</p>
          </div>
          <Link to="/therapist/patients">
            <Button className="gap-2 bg-primary hover:bg-primary/90 w-full sm:w-auto" data-testid="add-patient-btn">
              <Plus className="w-4 h-4" />{t("nav.addPatient")}
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
          {statCards.map((stat, idx) => {
            const cardContent = (
              <Card key={idx} className={`card-hover ${stat.link ? 'cursor-pointer' : ''}`}>
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div><p className="text-xs sm:text-sm text-muted-foreground">{stat.label}</p><p className="text-xl sm:text-3xl font-semibold mt-1 sm:mt-2">{stat.value}</p></div>
                    <div className={`p-2 sm:p-3 rounded-lg bg-muted ${stat.color}`}><stat.icon className="w-4 h-4 sm:w-5 sm:h-5" /></div>
                  </div>
                  {stat.link && <p className="text-xs text-primary mt-2 flex items-center gap-1">{t("therapist.viewAll")} <ArrowRight className="w-3 h-3" /></p>}
                </CardContent>
              </Card>
            );
            return stat.link ? <Link key={idx} to={stat.link}>{cardContent}</Link> : <div key={idx}>{cardContent}</div>;
          })}
        </div>

        <UpcomingAppointments />
        <RecentSessionsCard />

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="p-4 sm:p-6"><CardTitle className="font-heading text-lg sm:text-xl">{t("therapist.quickActions")}</CardTitle></CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 space-y-2 sm:space-y-3">
              <Link to="/therapist/patients" className="block">
                <div className="flex items-center justify-between p-3 sm:p-4 rounded-lg border hover:bg-muted transition-colors">
                  <div className="flex items-center gap-2 sm:gap-3"><Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" /><span className="text-sm sm:text-base">{t("therapist.managePatients")}</span></div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </Link>
              <Link to="/therapist/exercises" className="block">
                <div className="flex items-center justify-between p-3 sm:p-4 rounded-lg border hover:bg-muted transition-colors">
                  <div className="flex items-center gap-2 sm:gap-3"><BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-primary" /><span className="text-sm sm:text-base">{t("therapist.exerciseLibrary")}</span></div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </Link>
              <Link to="/therapist/reminders" className="block">
                <div className="flex items-center justify-between p-3 sm:p-4 rounded-lg border hover:bg-muted transition-colors">
                  <div className="flex items-center gap-2 sm:gap-3"><Bell className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    <div className="flex items-center gap-2">
                      <span>{t("therapist.reminders")}</span>
                      {reminderStats.pending_reminders > 0 && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">{reminderStats.pending_reminders} {t("common.pending").toLowerCase()}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Exercise Overview per Patient */}
        {(() => {
          const activePatients = patients.filter(p => p.is_active);
          const query = exerciseSearch.toLowerCase().trim();
          const filtered = query
            ? activePatients.filter(p => p.name?.toLowerCase().includes(query) || p.email?.toLowerCase().includes(query))
            : activePatients;
          const COLLAPSED_COUNT = 5;
          const showToggle = filtered.length > COLLAPSED_COUNT && !query;
          const displayed = (!exerciseListExpanded && !query) ? filtered.slice(0, COLLAPSED_COUNT) : filtered;
          const hiddenCount = filtered.length - COLLAPSED_COUNT;

          return activePatients.length > 0 && (
            <Card data-testid="exercise-overview">
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-3">
                <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  {t("therapist.exerciseOverview", "Resume des exercices par patient")}
                  <span className="text-sm font-normal text-muted-foreground">({activePatients.length})</span>
                </CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t("therapist.searchPatient", "Rechercher un patient...")}
                    value={exerciseSearch}
                    onChange={e => { setExerciseSearch(e.target.value); setExerciseListExpanded(true); }}
                    className="pl-9 h-9 text-sm"
                    data-testid="exercise-overview-search"
                  />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="exercise-overview-empty">
                    {t("therapist.noPatientFound", "Aucun patient trouve")}
                  </p>
                ) : (
                  <div className="divide-y">
                    {displayed.map(patient => {
                      const summary = exerciseSummaries[patient.user_id];
                      return (
                        <Link
                          key={patient.user_id}
                          to={`/therapist/patients/${patient.user_id}/exercise-review`}
                          className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-muted/50 transition-colors"
                          data-testid={`exercise-overview-${patient.user_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-medium text-sm">{patient.name?.charAt(0)?.toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="font-medium text-sm">{patient.name}</p>
                              {summary ? (
                                <p className="text-xs text-muted-foreground">
                                  {summary.total_entries} {t("therapist.entries", "entrees")} · {summary.exercise_types_active} {t("therapist.types", "types")}
                                </p>
                              ) : (
                                <p className="text-xs text-muted-foreground">{t("common.loading", "Chargement...")}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {summary && (
                              <div className="flex items-center gap-2 text-xs">
                                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                                  <CheckCircle className="w-3.5 h-3.5" /> {summary.completed_assignments}
                                </span>
                                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                  <Clock className="w-3.5 h-3.5" /> {summary.pending_assignments}
                                </span>
                              </div>
                            )}
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
                {showToggle && (
                  <Button
                    variant="ghost"
                    className="w-full mt-2 text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => setExerciseListExpanded(!exerciseListExpanded)}
                    data-testid="exercise-overview-toggle"
                  >
                    {exerciseListExpanded ? (
                      <><ChevronUp className="w-4 h-4 mr-1" /> {t("therapist.showLess", "Voir moins")}</>
                    ) : (
                      <><ChevronDown className="w-4 h-4 mr-1" /> {t("therapist.showMore", "Voir plus")} (+{hiddenCount})</>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Referral Program */}
        {referralInfo && (
          <Card data-testid="referral-section">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Gift className="w-5 h-5 text-primary" />
                {t("referral.title", "Programme de parrainage")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("referral.desc", "Parrainez des confrères et gagnez 1 mois gratuit pour chaque 10 thérapeutes parrainés qui s'abonnent.")}</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono truncate" data-testid="referral-code">{referralInfo.referral_code}</code>
                <Button variant="outline" size="sm" onClick={copyReferralLink} className="gap-1.5" data-testid="copy-referral-btn">
                  <Copy className="w-4 h-4" /> {t("referral.copyLink", "Copier le lien")}
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-semibold">{referralInfo.total_referred}</p>
                  <p className="text-xs text-muted-foreground">{t("referral.totalReferred", "Parrainés")}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-semibold">{referralInfo.paying_referred}</p>
                  <p className="text-xs text-muted-foreground">{t("referral.paying", "Abonnés")}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-semibold text-emerald-600">{referralInfo.free_months_available}</p>
                  <p className="text-xs text-muted-foreground">{t("referral.freeMonths", "Mois gratuits")}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-semibold">{referralInfo.progress_to_next}/10</p>
                  <p className="text-xs text-muted-foreground">{t("referral.progress", "Prochain bonus")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Owner: Invite Colleagues */}
        {isOwner && (
          <Card data-testid="owner-invite-section">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                {t("owner.inviteTitle", "Inviter un(e) confrère/consoeur")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("owner.inviteDesc", "Créez un lien d'invitation pour un(e) collègue thérapeute. Cette personne bénéficiera d'un accès gratuit sans abonnement. Chaque thérapeute gère ses propres patients de manière indépendante et confidentielle.")}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input placeholder={t("owner.colleagueName", "Nom du confrère/consoeur")} value={inviteName} onChange={e => setInviteName(e.target.value)} className="sm:w-48" data-testid="invite-name-input" />
                <Input placeholder="Email (optionnel)" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="sm:flex-1" type="email" data-testid="invite-email-input" />
                <Button onClick={handleOwnerInvite} disabled={inviting || !inviteName} className="gap-1.5" data-testid="send-invite-btn">
                  <UserPlus className="w-4 h-4" /> {t("owner.createInvite", "Créer l'invitation")}
                </Button>
              </div>
              {ownerInvitations.length > 0 && (
                <div className="space-y-2 mt-4">
                  <h4 className="text-sm font-medium">{t("owner.invitationsList", "Invitations envoyées")}</h4>
                  {ownerInvitations.map(inv => (
                    <div key={inv.code} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border bg-card" data-testid={`invitation-${inv.code}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{inv.invited_name || inv.invited_email || inv.code}</p>
                        <p className="text-xs text-muted-foreground">{inv.invited_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {inv.used ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> {t("owner.used", "Utilisée")}
                          </span>
                        ) : (
                          <>
                            <Button variant="outline" size="sm" className="gap-1 text-xs h-7" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/register?owner_invite=${inv.code}`); alert(t("owner.inviteCopied", "Lien copié !")); }}>
                              <Copy className="w-3 h-3" /> {t("owner.copyLink", "Copier")}
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0" onClick={() => revokeInvite(inv.code)} data-testid={`revoke-${inv.code}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TherapistDashboard;
